import type { TParams } from './types.ts';

/**
 * Render a template with `{name}` placeholders against a params object.
 *
 * Rules
 * - Unknown placeholders stay literal (`{foo}`) — surfaces missing data
 *   loudly in the UI instead of eating copy.
 * - Unused params are silently ignored; the translator might not need
 *   every datum provided by the caller in every locale.
 * - No escape sequences. Double braces are NOT honoured: `{{name}}`
 *   with `{ name: 'x' }` renders `{x}`, because the inner `{name}` is
 *   still a valid placeholder and gets substituted while the outer
 *   `{` passes through verbatim. Dictionary authors must therefore
 *   avoid literal braces in templates. If a future key genuinely needs
 *   one, introduce an escape protocol then and update this comment.
 *
 * Same implementation as `apps/console-web/src/i18n/format.ts` — the
 * two are kept in lockstep deliberately; duplicating 10 lines per
 * workspace is cheaper than sharing a `packages/i18n-runtime` just for
 * this.
 */
const PLACEHOLDER_PATTERN = /\{(\w+)\}/g;

export function substitute(template: string, params: TParams | undefined): string {
  if (params === undefined) {
    return template;
  }
  return template.replace(PLACEHOLDER_PATTERN, (match, rawKey: string) => {
    const value = params[rawKey];
    if (value === undefined) {
      // Keep the original `{key}` so missing data is visible in QA /
      // staging; a template that silently swallows missing params is
      // much harder to debug than one that screams.
      return match;
    }
    return String(value);
  });
}
